/**
 * VNC projection helper — projects the Net Book Value of each asset
 * category (fleet, depot, office, software, website) over a future
 * horizon. Reused by `VncEvolutionChart.jsx` (Amortissements tab).
 *
 * Single source of truth : leverages the existing depreciation functions
 * (`computeFleetDepreciation`, `computeCapexDepreciation`) by passing
 * forward-shifted `totalDays` so the projection is strictly equivalent to
 * what the in-game accounting would compute at each future date.
 *
 * 28/05/2026 v2 — Phase B bonus.
 */
import { computeFleetDepreciation } from './depreciation';
import { computeCapexDepreciation } from './capexDepreciation';

const DAYS_PER_MONTH = 30;

/**
 * @param {object}   state         Zustand state slice
 * @param {number[]} monthOffsets  e.g. [0, 3, 6, 9, ..., 36]
 * @returns {Array<{label, monthOffset, fleet, depot, office, software, website, total}>}
 */
export function projectVncSeries(state, monthOffsets) {
  const vehicles = state?.vehicles || [];
  const totalDays = state?.totalDays || 0;
  const activeOpt = state?.activeOptimizations || {};

  const result = [];
  for (const offset of monthOffsets) {
    const futureTotalDays = totalDays + offset * DAYS_PER_MONTH;
    const fleet = computeFleetDepreciation(vehicles, futureTotalDays, activeOpt);
    const capex = computeCapexDepreciation({
      ...state,
      totalDays: futureTotalDays,
    });
    const fleetVnc = Math.round(fleet.totalVnc);
    const depotVnc = Math.round(capex.byCategory.depot?.vnc || 0);
    const officeVnc = Math.round(capex.byCategory.office?.vnc || 0);
    const softwareVnc = Math.round(capex.byCategory.software?.vnc || 0);
    const websiteVnc = Math.round(capex.byCategory.website?.vnc || 0);
    result.push({
      label: offset === 0 ? 'Aujourd\'hui' : `+${offset}m`,
      monthOffset: offset,
      fleet: fleetVnc,
      depot: depotVnc,
      office: officeVnc,
      software: softwareVnc,
      website: websiteVnc,
      total: fleetVnc + depotVnc + officeVnc + softwareVnc + websiteVnc,
    });
  }
  return result;
}

/**
 * Default horizon for the Amortissements tab chart : current + every
 * 3 months up to +36 months (so 13 data points).
 */
export const DEFAULT_VNC_HORIZON_MONTHS = [0, 3, 6, 9, 12, 15, 18, 21, 24, 27, 30, 33, 36];
